import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Floating quick-notes widget.
// - Visible to every authenticated user across all internal pages.
// - Notes scope is *today only*; backend only returns today's notes, so they "expire" overnight.
export default function QuickNotesWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/quick-notes").then(({ data }) => setNotes(data)).catch(() => {});
  }, [user]);

  // Refresh at local midnight so notes flush automatically without a manual reload.
  useEffect(() => {
    if (!user || user === false) return;
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 30);
    const t = setTimeout(() => {
      api.get("/quick-notes").then(({ data }) => setNotes(data)).catch(() => {});
    }, tomorrow - now);
    return () => clearTimeout(t);
  }, [user, notes.length]);

  if (!user || user === false) return null;

  const add = async (e) => {
    e?.preventDefault?.();
    const v = text.trim();
    if (!v) return;
    try {
      const { data } = await api.post("/quick-notes", { text: v });
      setNotes((n) => [...n, data]);
      setText("");
      inputRef.current?.focus();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = async (n) => {
    try {
      const { data } = await api.patch(`/quick-notes/${n.id}`, { done: !n.done });
      setNotes((arr) => arr.map((x) => (x.id === n.id ? data : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (n) => {
    try {
      await api.delete(`/quick-notes/${n.id}`);
      setNotes((arr) => arr.filter((x) => x.id !== n.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remaining = notes.filter((n) => !n.done).length;

  return (
    <div
      className="fixed right-3 top-1/2 -translate-y-1/2 z-40 flex flex-col items-end"
      data-testid="quick-notes-widget"
    >
      {open && (
        <div className="mb-3 w-80 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden animate-fade-in">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-[#0F172A] text-white">
            <div>
              <div className="label-overline text-[#F54800]">Today</div>
              <div className="font-display text-sm font-bold">Quick notes</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white text-xs" data-testid="qn-close">close</button>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {notes.length === 0 && (
              <div className="px-4 py-6 text-xs text-slate-400 text-center">
                Nothing yet. Add something you want to ship today.
              </div>
            )}
            {notes.map((n) => (
              <div key={n.id} className="px-3 py-2 flex items-start gap-2 group">
                <input
                  type="checkbox"
                  checked={!!n.done}
                  onChange={() => toggle(n)}
                  className="mt-1 accent-[#F54800]"
                  data-testid={`qn-toggle-${n.id}`}
                />
                <div className={`flex-1 text-sm ${n.done ? "line-through text-slate-400" : "text-slate-800"}`}>{n.text}</div>
                <button onClick={() => remove(n)} className="opacity-0 group-hover:opacity-100 text-xs text-slate-400 hover:text-red-600" data-testid={`qn-del-${n.id}`}>×</button>
              </div>
            ))}
          </div>
          <form onSubmit={add} className="px-3 py-3 border-t border-slate-200 flex items-center gap-2">
            <input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ship a sketch, ping Priya…"
              className="flex-1 text-sm border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F54800]"
              data-testid="qn-input"
            />
            <button type="submit" className="text-xs bg-[#F54800] hover:bg-[#D63E00] text-white px-3 py-1.5 rounded-sm font-medium" data-testid="qn-add">Add</button>
          </form>
          <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100">
            Notes auto-clear at midnight · scope: only you
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="qn-fab"
        className="relative bg-[#F54800] hover:bg-[#D63E00] text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors"
        aria-label="Quick notes"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
        {remaining > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#0F172A] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {remaining}
          </span>
        )}
      </button>
    </div>
  );
}
